import React, { useEffect, useMemo, useState } from 'react';
import type { LicensePlan, LicenseStatus, SubscriptionStatusCode } from 'edinshall-licensing-shared';
import { usePortalContext } from '../context/PortalContext';
import { hasElevatedAccess, hasRole, PORTAL_ROLES } from '../utils/roles';

type AccountSummary = {
  subscriptionId: string;
  billingTenantId: string;
  plan: LicensePlan;
  status: SubscriptionStatusCode;
  activeCoverageCount: number;
  slotsAllocated: number | null;
  graceEndsAt?: string | null;
  updatedAt?: string | null;
};

type TenantEntitlementSummary = {
  tenantId: string;
  isBillingTenant: boolean;
  sourceStatus: LicenseStatus;
  entitlements: string[];
  products: Array<{ id: string; name: string }>;
  appliedOverrides: unknown[];
};

type CoverageSummary = {
  tenantId: string;
  isActive: boolean;
  registeredAt: string;
};

type CoverageResponse = {
  subscriptionId: string;
  billingTenantId: string;
  plan: LicensePlan;
  slotsAllocated: number | null;
  activeCoverageCount: number;
  coverage: CoverageSummary[];
};

const ClientAccounts = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManage = hasElevatedAccess(tenant?.roles, PORTAL_ROLES.manageClientAccounts);
  const canSuspend = hasRole(tenant?.roles, PORTAL_ROLES.suspendClientAccounts);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<AccountSummary[]>([]);
  const [counts, setCounts] = useState<Record<SubscriptionStatusCode, number>>({ active: 0, expired: 0, suspended: 0, banned: 0 });
  const [selected, setSelected] = useState<AccountSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Exclude<SubscriptionStatusCode, 'banned'>>('expired');
  const [graceDays, setGraceDays] = useState<number>(30);
  const [upgradeSlots, setUpgradeSlots] = useState<number>(5);
  const [reason, setReason] = useState('');
  const [createTenantId, setCreateTenantId] = useState('');
  const [createPlan, setCreatePlan] = useState<LicensePlan>('standard');
  const [createSlots, setCreateSlots] = useState<number>(5);
  const [createReason, setCreateReason] = useState('');
  const [tidHelperMessage, setTidHelperMessage] = useState<string | null>(null);
  const [tenantEntitlements, setTenantEntitlements] = useState<TenantEntitlementSummary[]>([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [coverage, setCoverage] = useState<CoverageSummary[]>([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [newCoveredTenantId, setNewCoveredTenantId] = useState('');
  const [coverageReason, setCoverageReason] = useState('');
  const [updatingCoverage, setUpdatingCoverage] = useState(false);

  const summaryText = useMemo(
    () => `Active: ${counts.active} • Expired: ${counts.expired} • Suspended: ${counts.suspended} • Banned: ${counts.banned}`,
    [counts]
  );

  const extractErrorMessage = async (response: Response, fallbackPrefix: string): Promise<string> => {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const payload = await response.json().catch(() => ({} as { message?: string; errorCode?: string }));
      const message = payload?.message;
      const code = payload?.errorCode;
      if (message && code) return `${message} [${code}]`;
      if (message) return message;
      if (code) return `${fallbackPrefix} (${response.status}) [${code}]`;
      return `${fallbackPrefix} (${response.status})`;
    }
    const text = (await response.text().catch(() => '')).trim();
    if (text.length > 0) {
      return `${fallbackPrefix} (${response.status}): ${text.slice(0, 220)}`;
    }
    return `${fallbackPrefix} (${response.status})`;
  };

  const load = async (search?: string): Promise<void> => {
    if (!canManage) return;
    setLoading(true);
    setError(null);
    try {
      const url = search ? `/api/admin/client-accounts?q=${encodeURIComponent(search)}` : '/api/admin/client-accounts';
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Client accounts request failed (${response.status})`);
      }
      const data = await response.json();
      setItems(data.items ?? []);
      setCounts({ active: 0, expired: 0, suspended: 0, banned: 0, ...(data.counts ?? {}) });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [canManage]);

  const handleSearch = async (): Promise<void> => {
    await load(query.trim());
  };

  const isGuid = (value: string): boolean =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value.trim());

  const useCurrentTenantTid = (): void => {
    const currentTid = tenant?.tenantId?.trim() ?? '';
    if (!currentTid) {
      setTidHelperMessage('Current session does not include a tenant tid.');
      return;
    }
    if (!isGuid(currentTid)) {
      setTidHelperMessage('Current session tenant tid is not a valid GUID.');
      return;
    }
    setCreateTenantId(currentTid);
    setTidHelperMessage('Using tenant tid from current session.');
  };

  const handleSelect = (item: AccountSummary): void => {
    setSelected(item);
    setStatus(item.status === 'banned' ? 'expired' : item.status);
    setGraceDays(30);
    setUpgradeSlots(Math.max(5, item.slotsAllocated ?? 5));
    setReason('');
    void loadEntitlements(item.subscriptionId);
    void loadCoverage(item.subscriptionId);
  };

  const loadEntitlements = async (subscriptionId: string): Promise<void> => {
    setEntitlementsLoading(true);
    try {
      const response = await fetch(`/api/admin/client-accounts/${subscriptionId}/entitlements`);
      if (!response.ok) {
        throw new Error(`Entitlements request failed (${response.status})`);
      }
      const payload = await response.json();
      setTenantEntitlements(Array.isArray(payload.tenantEntitlements) ? payload.tenantEntitlements : []);
    } catch (err) {
      setTenantEntitlements([]);
      setError((err as Error).message);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const loadCoverage = async (subscriptionId: string): Promise<void> => {
    setCoverageLoading(true);
    try {
      const response = await fetch(`/api/admin/client-accounts/${subscriptionId}/coverage`);
      if (!response.ok) {
        throw new Error(`Coverage request failed (${response.status})`);
      }
      const payload = await response.json() as CoverageResponse;
      setCoverage(Array.isArray(payload.coverage) ? payload.coverage : []);
    } catch (err) {
      setCoverage([]);
      setError((err as Error).message);
    } finally {
      setCoverageLoading(false);
    }
  };

  const addCoverage = async (): Promise<void> => {
    if (!selected || !newCoveredTenantId.trim()) return;
    setUpdatingCoverage(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/coverage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: newCoveredTenantId.trim(),
          reason: coverageReason || undefined
        })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Coverage add failed'));
      }
      setNewCoveredTenantId('');
      await load(query.trim());
      await Promise.all([
        loadCoverage(selected.subscriptionId),
        loadEntitlements(selected.subscriptionId)
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUpdatingCoverage(false);
    }
  };

  const removeCoverage = async (tenantId: string): Promise<void> => {
    if (!selected) return;
    setUpdatingCoverage(true);
    setError(null);
    try {
      const reasonQuery = coverageReason.trim() ? `?reason=${encodeURIComponent(coverageReason.trim())}` : '';
      const response = await fetch(
        `/api/admin/client-accounts/${selected.subscriptionId}/coverage/${encodeURIComponent(tenantId)}${reasonQuery}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Coverage remove failed'));
      }
      await load(query.trim());
      await Promise.all([
        loadCoverage(selected.subscriptionId),
        loadEntitlements(selected.subscriptionId)
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUpdatingCoverage(false);
    }
  };

  const updateStatus = async (): Promise<void> => {
    if (!selected) return;
    if (selected.status === 'banned') {
      setError('Banned subscriptions cannot be changed from the manual client-account controls.');
      return;
    }
    if (status === 'suspended' && !canSuspend) {
      setError('Suspend action requires SuspendClientAccounts role.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, graceDays, reason: reason || undefined })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Update failed'));
      }
      const updated = await response.json();
      setSelected(updated);
      await load(query.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const updateGrace = async (): Promise<void> => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/grace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ graceDays, reason: reason || undefined })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Grace update failed'));
      }
      const updated = await response.json();
      setSelected(updated);
      await load(query.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const upgradePlan = async (): Promise<void> => {
    if (!selected) return;
    if (selected.plan !== 'standard') {
      setError('Only standard subscriptions can be upgraded.');
      return;
    }
    if (!Number.isInteger(upgradeSlots) || upgradeSlots < 5) {
      setError('Enterprise should allocate at least 5 tenant slots.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: upgradeSlots, reason: reason || undefined })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Upgrade failed'));
      }
      const updated = await response.json();
      setSelected(updated);
      await load(query.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const updateSlots = async (): Promise<void> => {
    if (!selected || selected.plan !== 'enterprise') return;
    if (!Number.isInteger(upgradeSlots) || upgradeSlots < 5) {
      setError('Enterprise should allocate at least 5 tenant slots.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: upgradeSlots, reason: reason || undefined })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Slot update failed'));
      }
      const updated = await response.json();
      setSelected(updated);
      await load(query.trim());
      await Promise.all([
        loadCoverage(updated.subscriptionId),
        loadEntitlements(updated.subscriptionId)
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const downgradePlan = async (): Promise<void> => {
    if (!selected || selected.plan !== 'enterprise') return;
    const warning = selected.activeCoverageCount > 1
      ? 'Downgrading to standard will collapse coverage to the billing tenant only. Additional covered tenants will be removed and may need to be reallocated later. Continue?'
      : 'Downgrading to standard will reduce this subscription to one tenant slot on the billing tenant. Continue?';
    if (!window.confirm(warning)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/client-accounts/${selected.subscriptionId}/downgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined })
      });
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Downgrade failed'));
      }
      const updated = await response.json();
      setSelected(updated);
      await load(query.trim());
      await Promise.all([
        loadCoverage(updated.subscriptionId),
        loadEntitlements(updated.subscriptionId)
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async (): Promise<void> => {
    if (!createTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        billingTenantId: createTenantId.trim(),
        plan: createPlan,
        slots: createPlan === 'enterprise' ? createSlots : undefined,
        reason: createReason || undefined
      };
      const response = await fetch('/api/admin/client-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const payloadError = await response.json().catch(() => ({}));
        throw new Error(payloadError?.message || `Create failed (${response.status})`);
      }
      const created = await response.json();
      setSelected(created);
      void loadEntitlements(created.subscriptionId);
      void loadCoverage(created.subscriptionId);
      setCreateReason('');
      await load(query.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="card">
        <h1 className="section-title">Client Accounts</h1>
        <p className="muted">You do not have permission to manage client accounts.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">Client Accounts</h1>
      <p className="muted">Manage subscription status and read-only grace access.</p>

      <div className="card" style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="input"
            placeholder="Search by billing tenant or subscription ID"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ flex: '1 1 320px' }}
          />
          <button className="btn" type="button" onClick={handleSearch} disabled={loading}>
            Search
          </button>
          <span className="badge-subtle">{summaryText}</span>
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Create Temporary Subscription</h3>
        <p className="muted" style={{ marginTop: '4px' }}>
          Creates a SQL-backed license record used by `/license/status`.
        </p>
        <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              placeholder="Billing tenant ID (GUID)"
              value={createTenantId}
              onChange={(event) => {
                setCreateTenantId(event.target.value);
                setTidHelperMessage(null);
              }}
              style={{ flex: '1 1 320px' }}
            />
            <button className="btn secondary" type="button" onClick={useCurrentTenantTid}>
              Use current tenant tid
            </button>
          </div>
          <p className="muted" style={{ marginTop: '0' }}>
            billingTenantId must match the Entra tenant id (tid) of the SharePoint tenant being licensed.
          </p>
          {tidHelperMessage && (
            <div className="badge-subtle">{tidHelperMessage}</div>
          )}
          {createTenantId.trim().length > 0 && !isGuid(createTenantId) && (
            <div className="pill warning">billingTenantId must be a valid GUID.</div>
          )}
          <select
            className="input"
            value={createPlan}
            onChange={(event) => {
              const nextPlan = event.target.value as typeof createPlan;
              setCreatePlan(nextPlan);
              if (nextPlan === 'enterprise') {
                setCreateSlots((current) => Math.max(5, current || 5));
              }
            }}
          >
            <option value="free">free</option>
            <option value="standard">standard</option>
            <option value="enterprise">enterprise</option>
          </select>
          {createPlan === 'enterprise' && (
            <>
              <input
                className="input"
                type="number"
                min={5}
                placeholder="Slots allocated"
                value={createSlots}
                onChange={(event) => setCreateSlots(Number(event.target.value))}
              />
              <p className="muted" style={{ marginTop: '0' }}>
                Enterprise subscriptions default to 5 tenant slots.
              </p>
            </>
          )}
          <input
            className="input"
            placeholder="Reason (optional)"
            value={createReason}
            onChange={(event) => setCreateReason(event.target.value)}
          />
          <button
            className="btn"
            type="button"
            onClick={createAccount}
            disabled={loading || !isGuid(createTenantId) || (createPlan === 'enterprise' && createSlots < 5)}
          >
            Create subscription
          </button>
        </div>
      </div>

      {error && <div className="pill warning" style={{ marginTop: '12px' }}>{error}</div>}
      {loading && <div className="badge-subtle" style={{ marginTop: '12px' }}>Loading accounts…</div>}

      <div className="grid columns-2" style={{ marginTop: '12px' }}>
        <div className="card">
          <h3>Accounts</h3>
          {items.length === 0 && <p className="muted">No accounts found.</p>}
          {items.map((item) => (
            <button
              key={item.subscriptionId}
              type="button"
              className="list-row"
              onClick={() => handleSelect(item)}
            >
              <div>
                <strong>{item.billingTenantId}</strong>
                <div className="muted" style={{ fontSize: '0.85rem' }}>{item.subscriptionId}</div>
              </div>
              <div>
                <span className={`pill ${item.status === 'active' ? 'success' : item.status === 'expired' ? 'warning' : 'danger'}`}>
                  {item.status}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="card">
          <h3>Selected Account</h3>
          {!selected && <p className="muted">Select a tenant to manage.</p>}
          {selected && (
            <div style={{ display: 'grid', gap: '10px' }}>
              <div>
                <div className="muted">Billing tenant</div>
                <div>{selected.billingTenantId}</div>
              </div>
              <div>
                <div className="muted">Plan / status</div>
                <div>{selected.plan} / {selected.status}</div>
              </div>
              {selected.plan === 'enterprise' && (
                <div className="card" style={{ marginTop: '4px' }}>
                  <strong>Tenant slot allocation</strong>
                  <div className="muted" style={{ marginTop: '6px' }}>
                    Active coverage: {selected.activeCoverageCount} / {selected.slotsAllocated ?? 0} allocated tenant slots
                  </div>
                  <div style={{ display: 'grid', gap: '6px', marginTop: '10px' }}>
                    <label className="muted">Allocated tenant slots</label>
                    <input
                      className="input"
                      type="number"
                      min={5}
                      value={upgradeSlots}
                      onChange={(event) => setUpgradeSlots(Number(event.target.value))}
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn"
                        type="button"
                        onClick={updateSlots}
                        disabled={loading || upgradeSlots < Math.max(5, selected.slotsAllocated ?? 5)}
                      >
                        Update slot allocation
                      </button>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={downgradePlan}
                        disabled={loading}
                      >
                        Downgrade to standard
                      </button>
                    </div>
                    <p className="muted" style={{ marginTop: '0' }}>
                      Enterprise defaults to 5 tenant slots. Slot allocation can be increased here. Reducing slots is handled through downgrade.
                    </p>
                    {selected.activeCoverageCount > 1 && (
                      <div className="pill warning">
                        Downgrading to standard will remove additional covered tenants and retain only the billing tenant.
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
                    <input
                      className="input"
                      placeholder="Covered tenant ID (GUID)"
                      value={newCoveredTenantId}
                      onChange={(event) => setNewCoveredTenantId(event.target.value)}
                    />
                    <input
                      className="input"
                      placeholder="Coverage change reason (optional)"
                      value={coverageReason}
                      onChange={(event) => setCoverageReason(event.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={addCoverage}
                        disabled={updatingCoverage || !isGuid(newCoveredTenantId)}
                      >
                        {updatingCoverage ? 'Updating…' : 'Add covered tenant'}
                      </button>
                    </div>
                    {coverageLoading && <span className="badge-subtle">Loading tenant allocation…</span>}
                    {!coverageLoading && coverage.length === 0 && (
                      <span className="badge-subtle">No tenant coverage records found.</span>
                    )}
                    {!coverageLoading && coverage.map((entry) => {
                      const isBillingTenant = entry.tenantId === selected.billingTenantId;
                      return (
                        <div key={entry.tenantId} className="list-row" style={{ marginTop: 0 }}>
                          <div>
                            <strong>{entry.tenantId}</strong>
                            <div className="muted" style={{ fontSize: '0.85rem' }}>
                              {isBillingTenant ? 'Billing tenant' : entry.isActive ? 'Covered tenant' : 'Inactive coverage'}
                              {' • '}
                              Registered {new Date(entry.registeredAt).toLocaleString()}
                            </div>
                          </div>
                          {!isBillingTenant && entry.isActive ? (
                            <button
                              className="btn secondary"
                              type="button"
                              onClick={() => void removeCoverage(entry.tenantId)}
                              disabled={updatingCoverage}
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="badge-subtle">{isBillingTenant ? 'Required' : entry.isActive ? 'Active' : 'Inactive'}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {selected.plan !== 'enterprise' && (
                <div style={{ display: 'grid', gap: '6px' }}>
                  <label className="muted">Upgrade to enterprise (slots)</label>
                  <input
                    className="input"
                    type="number"
                    min={5}
                    value={upgradeSlots}
                    onChange={(event) => setUpgradeSlots(Number(event.target.value))}
                  />
                  <p className="muted" style={{ marginTop: '0' }}>
                    Enterprise upgrades default to 5 tenant slots.
                  </p>
                  <button className="btn" type="button" onClick={upgradePlan} disabled={loading || upgradeSlots < 5}>
                    Upgrade to enterprise
                  </button>
                </div>
              )}
              <div>
                <div className="muted">Grace ends</div>
                <div>{selected.graceEndsAt ?? '—'}</div>
              </div>

              <div style={{ display: 'grid', gap: '6px' }}>
                <label className="muted">Set status</label>
                <select className="input" value={status} onChange={(event) => setStatus(event.target.value as typeof status)} disabled={selected.status === 'banned'}>
                  <option value="active">active</option>
                  <option value="expired">expired</option>
                  <option value="suspended" disabled={!canSuspend}>suspended</option>
                </select>
                {selected.status === 'banned' && (
                  <p className="muted" style={{ marginTop: '4px' }}>
                    Banned subscriptions are read-only in these manual controls.
                  </p>
                )}
                {!canSuspend && (
                  <p className="muted" style={{ marginTop: '4px' }}>
                    Suspend requires SuspendClientAccounts role.
                  </p>
                )}
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                <label className="muted">Grace days (1-9999)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={9999}
                  value={graceDays}
                  onChange={(event) => setGraceDays(Number(event.target.value))}
                />
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                <label className="muted">Reason (optional)</label>
                <input
                  className="input"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn" type="button" onClick={updateStatus} disabled={loading || selected.status === 'banned'}>
                  Update status
                </button>
                <button className="btn secondary" type="button" onClick={updateGrace} disabled={loading || selected.status === 'banned'}>
                  Update grace only
                </button>
              </div>
              <p className="muted" style={{ marginTop: '6px' }}>
                Suspended tenants receive no grace. Grace applies only to expired subscriptions.
              </p>

              <div style={{ marginTop: '8px' }}>
                <h4 style={{ marginBottom: '6px' }}>Resolved entitlements (by covered tenant)</h4>
                {entitlementsLoading && <div className="badge-subtle">Loading entitlements…</div>}
                {!entitlementsLoading && tenantEntitlements.length === 0 && (
                  <p className="muted">No entitlement data available for this subscription.</p>
                )}
                {!entitlementsLoading && tenantEntitlements.map((tenant) => (
                  <div key={tenant.tenantId} className="list-row" style={{ marginBottom: '8px' }}>
                    <div>
                      <strong>{tenant.tenantId}</strong>
                      <div className="muted" style={{ fontSize: '0.85rem' }}>
                        {tenant.isBillingTenant ? 'Billing tenant' : 'Covered tenant'} • Source status: {tenant.sourceStatus}
                      </div>
                      <div className="muted" style={{ fontSize: '0.85rem' }}>
                        Products: {tenant.products.length > 0 ? tenant.products.map((p) => p.name).join(', ') : 'none'}
                      </div>
                      <div className="muted" style={{ fontSize: '0.85rem' }}>
                        Entitlements: {tenant.entitlements.length > 0 ? tenant.entitlements.join(', ') : 'none'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientAccounts;
